import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { GitCompare, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import apiClient from '@/services/api/client';

function fmtCurrency(v: number): string { return v >= 0 ? `$${v.toLocaleString()}` : `-$${Math.abs(v).toLocaleString()}`; }
const TOOLTIP_STYLE = { contentStyle: { backgroundColor: 'rgba(30,41,59,0.95)', border: 'none', borderRadius: 8, color: '#e2e8f0', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }, itemStyle: { color: '#94a3b8' }, wrapperStyle: { outline: 'none' as const }, cursor: false };
const MAT_COLORS = { high: '#ef4444', medium: '#f59e0b', low: '#6b7280' };

export default function SmartComparisonPage() {
  const [selectedClientId, setSelectedClientId] = useState('');
  const [reportType, setReportType] = useState('pnl');

  const { data: clientsData } = useQuery({
    queryKey: ['clients-for-comparison'],
    queryFn: async () => { const r = await apiClient.get('/clients?limit=500'); return r.data.data; },
  });
  const clients = (clientsData?.clients || clientsData || []).filter((c: any) => c.xeroOrgId || c.xero_org_id);

  const compareMutation = useMutation({
    mutationFn: async () => {
      const r = await apiClient.post('/comparison/analyze', { clientId: selectedClientId, reportType });
      return r.data.data;
    },
    onError: () => toast.error('Failed to analyze'),
  });

  const data = compareMutation.data;
  const lineItems = data?.lineItems || [];
  const highItems = lineItems.filter((i: any) => i.materiality === 'high').slice(0, 10);

  const chartData = highItems.map((i: any) => ({
    name: i.account.length > 20 ? i.account.substring(0, 18) + '...' : i.account,
    variance: i.variance,
    fill: i.variance > 0 ? '#22c55e' : '#ef4444',
  }));

  return (
    <div className="space-y-6">
      <PageHeader
        icon={GitCompare}
        title="Smart Document Comparison"
        description="AI-powered year-over-year variance analysis with materiality assessment"
        iconColor="bg-indigo-500/10 text-indigo-500"
      />


      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center gap-4 flex-wrap">
            <Select value={selectedClientId} onValueChange={setSelectedClientId}>
              <SelectTrigger className="w-[300px]"><SelectValue placeholder="Select client..." /></SelectTrigger>
              <SelectContent className="max-h-[300px]">{clients.map((c: any) => <SelectItem key={c.id} value={c.id}>{c.displayName || c.display_name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pnl">Profit & Loss</SelectItem>
                <SelectItem value="bs">Balance Sheet</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => compareMutation.mutate()} disabled={!selectedClientId || compareMutation.isPending}>
              {compareMutation.isPending ? 'Analyzing...' : 'Compare Periods'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {compareMutation.isPending && (
        <div className="flex items-center justify-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /><span className="ml-3 text-sm text-muted-foreground">Fetching and comparing financial data...</span></div>
      )}

      {data && (
        <>
          {/* Summary KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card><CardContent className="pt-3 pb-3">
              <p className="text-[10px] text-muted-foreground uppercase">Revenue Change</p>
              <p className={cn('text-lg font-bold', (data.summary?.revenueChange || 0) >= 0 ? 'text-green-500' : 'text-red-500')}>
                {data.summary?.revenueChange >= 0 ? '+' : ''}{data.summary?.revenueChangePercent}%
              </p>
              <p className="text-[10px] text-muted-foreground">{fmtCurrency(data.summary?.revenueChange || 0)}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-3 pb-3">
              <p className="text-[10px] text-muted-foreground uppercase">Profit Change</p>
              <p className={cn('text-lg font-bold', (data.summary?.profitChange || 0) >= 0 ? 'text-green-500' : 'text-red-500')}>
                {data.summary?.profitChange >= 0 ? '+' : ''}{data.summary?.profitChangePercent}%
              </p>
              <p className="text-[10px] text-muted-foreground">{fmtCurrency(data.summary?.profitChange || 0)}</p>
            </CardContent></Card>
            <Card className="border-red-500/30"><CardContent className="pt-3 pb-3">
              <p className="text-[10px] text-red-500 uppercase">High Materiality</p>
              <p className="text-lg font-bold text-red-500">{data.summary?.highMateriality || 0}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-3 pb-3">
              <p className="text-[10px] text-muted-foreground uppercase">Total Line Items</p>
              <p className="text-lg font-bold">{data.summary?.totalLineItems || 0}</p>
            </CardContent></Card>
          </div>

          {/* Variance Chart */}
          {chartData.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Top Material Variances</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={Math.max(200, chartData.length * 35)}>
                  <BarChart data={chartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                    <XAxis type="number" tick={{ fontSize: 10, fill: '#888' }} tickFormatter={v => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: '#999' }} width={120} />
                    <Tooltip {...TOOLTIP_STYLE} formatter={(v: number) => [fmtCurrency(v), 'Variance']} />
                    <Bar dataKey="variance" radius={[0, 4, 4, 0]} activeBar={false}>
                      {chartData.map((d: any, i: number) => <Cell key={i} fill={d.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* AI Commentary */}
          {data.aiCommentary && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Analyst Commentary</CardTitle><CardDescription className="text-xs">AI-generated analysis of significant variances</CardDescription></CardHeader>
              <CardContent>
                <div className="prose prose-sm dark:prose-invert max-w-none text-xs">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.aiCommentary}</ReactMarkdown>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Line Items Table */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Variance Detail ({data.periods?.prior} → {data.periods?.current})</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="border-b bg-muted/30">
                    <th className="px-3 py-2 text-left font-medium w-6"></th>
                    <th className="px-3 py-2 text-left font-medium">Account</th>
                    <th className="px-3 py-2 text-right font-medium">{data.periods?.prior}</th>
                    <th className="px-3 py-2 text-right font-medium">{data.periods?.current}</th>
                    <th className="px-3 py-2 text-right font-medium">Variance $</th>
                    <th className="px-3 py-2 text-right font-medium">Variance %</th>
                    <th className="px-3 py-2 text-center font-medium">Materiality</th>
                  </tr></thead>
                  <tbody>
                    {lineItems.filter((i: any) => i.materiality !== 'low').map((item: any, idx: number) => (
                      <tr key={idx} className="border-b hover:bg-muted/20">
                        <td className="px-3 py-1.5">
                          {item.direction === 'increase' ? <ArrowUpRight className="h-3 w-3 text-green-500" /> :
                           item.direction === 'decrease' ? <ArrowDownRight className="h-3 w-3 text-red-500" /> :
                           item.direction === 'new' ? <CheckCircle2 className="h-3 w-3 text-blue-500" /> :
                           <Minus className="h-3 w-3 text-muted-foreground" />}
                        </td>
                        <td className="px-3 py-1.5 font-medium">{item.account}</td>
                        <td className="px-3 py-1.5 text-right text-muted-foreground">{fmtCurrency(item.priorAmount)}</td>
                        <td className="px-3 py-1.5 text-right">{fmtCurrency(item.currentAmount)}</td>
                        <td className={cn('px-3 py-1.5 text-right font-medium', item.variance > 0 ? 'text-green-500' : item.variance < 0 ? 'text-red-500' : '')}>
                          {item.variance > 0 ? '+' : ''}{fmtCurrency(item.variance)}
                        </td>
                        <td className={cn('px-3 py-1.5 text-right', item.variancePercent > 0 ? 'text-green-500' : item.variancePercent < 0 ? 'text-red-500' : '')}>
                          {item.variancePercent > 0 ? '+' : ''}{item.variancePercent}%
                        </td>
                        <td className="px-3 py-1.5 text-center">
                          <Badge style={{ backgroundColor: MAT_COLORS[item.materiality as keyof typeof MAT_COLORS] + '20', color: MAT_COLORS[item.materiality as keyof typeof MAT_COLORS], border: 'none' }} className="text-[9px]">{item.materiality}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
